<template>
  <div class="sm-mail-group">
    <div class="sm-wrap">
      <breadcrumbs-block 
        v-if="!isMobile"
        :crumbs="breadcrumbs"
      />
      <div class="sm-mail-group__top">
        <h2>{{pageTitle}}</h2>
        <div 
          v-if="!isNew"
          class="sm-mail-group__top-right"
          :class="{'edit': edit}"
        >
          <template v-if="edit && viewPort == 'large'">
            <btn-block
              :type="'grey-cut'"
              :text="'Close editing'"
              @click="closeEdit"
            />
            <btn-block
              :type="'small'"
              :text="'Save changes'"
              @click="saveGroupItem"
            />
          </template>
          <template v-if="!edit">
            <btn-block
              :type="'edit'"
              :icon="'icon-edit'"
              :text="'Edit group'"
              @click="openEdit"
            />
          </template>
        </div>
      </div>

      <mail-list 
        :list="mailList"
        :showActions="false"
        :showCheckboxes="isNew || edit"
        :preselectedItems="preselectedItems"
        :noSort="true"
        @select="selectedItems = $event"
      />
    </div>

    <div
      class="sm-mail-group__controls"
      :class="{'active': (isNew && selectedItems.length) || (edit && viewPort == 'small')}"
    >
      <div class="sm-wrap">
        <div 
          class="sm-mail-group__controls-inner"
          :class="{'edit': edit}"
        >
          <template v-if="isNew">
            <btn-block 
              :type="'small'" 
              :text="'Create GroupMail'"
              @click="handleCreate"
            />
            <btn-block 
              :type="'reset'" 
              :class="'sm-btn--small'"
              :text="'Discard'" 
              @click="$router.push('mail?tab=group')"
            />
          </template>
          <template v-else>
            <btn-block
              :type="'grey-cut'"
              :text="'Close editing'"
              @click="closeEdit"
            />
            <btn-block
              :type="'small'"
              :text="'Save changes'"
              @click="saveGroupItem"
            />
          </template>
        </div>
      </div>
    </div>
    <popup-block
      v-if="popup.isOpen"
      :open="popup.isOpen"
      :size="'large'"
      :class="'sm-mail-group__popup'"
      @close="popup.isOpen = false;"
    >
      <template slot="popupContent">
        <div 
          v-if="popup.type == 'pmb'"
          class="sm-mail-group__popup-content"
        >
          <h4>New GroupMail</h4>
          <p>Select {{provider.client_level != 1 ? 'customer' : 'user'}} whose mail you’d like to group</p>
          <input-pmb-tags
            :ref="'tags'"
            :placeholder="'Name, Mailbox# or Company name'"
            @updateList="selectPMB($event)"
          />
        </div>
        <div 
          v-if="popup.type == 'pmb-reselect'"
          class="sm-mail-group__popup-content"
        >
          <h4>No available Mail Items for selected {{provider.client_level != 1 ? 'customer' : 'user'}}</h4>
          <p>Please select another {{provider.client_level != 1 ? 'customer' : 'user'}} whose mail you’d like to group or close popup to return</p>
          <input-pmb-tags
            :placeholder="'Name, Mailbox# or Company name'"
            @updateList="selectPMB($event)"
          />
        </div>
        <div
          v-if="popup.type == 'create'"
          class="sm-mail-group__popup-content"
        >
          <h4>GroupMail confirmation</h4>
          <p>Select an option:</p>
          <radiobutton-block
            :name="'create-option'"
            :text="'Create GroupMail and forward now'"
            :checked="afterCreate == 'forward'"
            @input="afterCreate = 'forward'"
          />
          <radiobutton-block
            :name="'create-option'"
            :text="'Create GroupMail and forward later'"
            :checked="afterCreate == ''"
            @input="afterCreate = ''"
          />
          <radiobutton-block
            :name="'create-option'"
            :text="'Create GroupMail and set reminder'"
            :checked="afterCreate == 'reminder'"
            @input="afterCreate = 'reminder'"
          />
          <datepicker-wrap
            v-if="afterCreate == 'reminder'"
            v-model="reminderDate"
            :placeholder="'Remind on'"
            :notBefore="new Date().setDate(new Date().getDate() - 1)"
          />
          <div class="sm-mail-group__popup-content-flex">
            <btn-block
              :type="'small'"
              :text="'Cancel'"
              :class="'sm-btn--grey'"
              @click="popup.isOpen = false"
            />
            <btn-block
              :type="'small'"
              :text="'Save'"
              :class="{
                'disabled': afterCreate == 'reminder' && !reminderDate
              }"
              @click="createGroupMail"
            />
          </div>
        </div>
      </template>
    </popup-block>

    <sm-spinner :show="showSpinner" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

import SingleMailList from '@/views/admin/pages/mail/components/SingleMailList';

import Breadcrumbs from '@/components/Breadcrumbs';
import Btn from '@/components/Buttons';
import Popup from '@/components/Popup';
import Spinner from '@/components/Spinner';
import InputPMBTags from '@/components/mail/InputPMBTags';
import ViewPort from '@/mixins/ViewPort';
import Radiobutton from '@/components/Radiobutton';
import DatepickerWrap from '@/components/DatepickerWrap';

export default {
  name: 'mail-group',
  components: {
    'mail-list': SingleMailList,
    'breadcrumbs-block': Breadcrumbs,
    'btn-block': Btn,
    'popup-block': Popup,
    'sm-spinner': Spinner,
    'input-pmb-tags': InputPMBTags,
    'radiobutton-block': Radiobutton,
    'datepicker-wrap': DatepickerWrap
  },
  data() {
    return {
      curItem: null,
      curPMB: null,
      pageTitle: '',
      edit: false,
      breadcrumbs: [
        {
          link: 'mail?tab=group',
          text: 'Mail Group'
        },
      ],
      selectedItems: [],
      preselectedItems: [],
      mailList: [],
      allowedBreakpoints: [
        'small',
        'large'
      ],
      popup: {
        isOpen: false,
        type: '', // values: pmb, pmb-reselect, create
      },
      showSpinner: false,
      afterCreate: 'forward',
      reminderDate: ''
    };
  },
  created() {
    this.showSpinner = true;

    if (!this.isNew) {
      let item_id = this.$route.query.id;

      if (!item_id)
        this.$router.push('mail?tab=group');

      this.getAdminGroupMailItem(item_id)
        .then(res => {
          this.curItem = res;
          this.mailList = this.curItem.mail_items;
          this.preselectedItems = this.mailList.map(item => item.id);
          
          let pmb_number = this.mailList[0].account_number;

          this.findPMBs(pmb_number)
            .then(res => {
              this.curPMB = res.find(item => item.pmb == pmb_number);

              this.fillPageData();
              this.showSpinner = false;
            })

        });
      
    } else {
      this.popup.type = 'pmb';
      this.popup.isOpen = true;
      this.fillPageData();
      this.showSpinner = false;

      setTimeout(() => {
        if (this.$refs.tags && this.$refs.tags.$el)
          this.$refs.tags.$el.querySelector('input').focus();
      }, 100);
    }    
  },
  computed: {
    ...mapGetters({
      isMobile: 'isMobile',
      provider: 'provider'
    }),
    isNew() {
      return this.$route.path.indexOf('new') > 0;
    }
  },
  mixins: [ViewPort],
  methods: {
    ...mapActions({
      getAdminGroupMailItem: 'mail/getAdminGroupMailItem',
      findPMBs: 'mail/findPMBs',
      getAdminList: 'mail/getAdminList',
      changeAdminGroupMail: 'mail/changeAdminGroupMail',
      createAdminGroupMail: 'mail/createAdminGroupMail'
    }),
    fillPageData() {
      if (!this.isNew) {
        this.breadcrumbs.push({
          link: `mail-group?id=${this.curItem.id}`,
          text: `Group mail #${this.curItem.id}`,
          current: true
        });
        this.pageTitle = `Group Mail #${this.curItem.id}`;
      } else {
        this.edit = true;
        this.breadcrumbs.push({
          link: 'new-mail-group',
          text: 'New GroupMail',
          current: true
        });
        this.pageTitle = 'Select Mail Items you\'d like to ship';
      }
    },
    selectPMB(data) {
      if (!data.length)
        return;

      this.showSpinner = true;
      let pmb_number = data[0];
      this.findPMBs(pmb_number)
        .then(res => {
          this.curPMB = res.find(item => item.pmb == pmb_number);
          this.showSpinner = false;

          this.openEdit();
        })
    },
    openEdit() {
      this.showSpinner = true;
      this.getAdminList({
        account_id: this.mailList.length ? this.mailList[0].account_id : '',
        search: this.curPMB.pmb,
        limit: 100 // TEMP
      })
        .then(res => {
          this.mailList = res.mail_items.filter(item => {
            if (item.account_number != this.curPMB.pmb)
              return false;

            if (item.status != 'processed' && item.status != 'junk')
              return true;

            if (
              item.status == 'processed' && 
              item.status_label &&
              !item.status_label.toLowerCase().includes('forwarded') && 
              !item.status_label.toLowerCase().includes('picked up')
            )
              return true;

            return false;
          });

          this.popup.isOpen = false;
          this.edit = true;
          this.showSpinner = false;

          if (this.isNew && !this.mailList.length) {
            this.popup.type = 'pmb-reselect';
            this.popup.isOpen = true;
          }
        });
    },
    closeEdit() {
      this.edit = false;
      this.mailList = this.curItem.mail_items;
    },
    saveGroupItem() {
      if (!this.selectedItems)
        return; // TEMP add notification

      this.showSpinner = true;
      this.changeAdminGroupMail({
        id: this.curItem.id,
        params: {
          mail_item_ids: this.selectedItems
        }
      })
        .then(res => {
          this.curItem = res;
          this.edit = false;
          this.mailList = this.curItem.mail_items;
          this.preselectedItems = this.mailList.map(item => item.id);
          this.showSpinner = false;
        })
    },
    handleCreate() {
      this.popup.type = 'create';
      this.popup.isOpen = true;
    },
    createGroupMail() {
      if (!this.selectedItems)
        return; // TEMP add notification
        
      this.showSpinner = true;

      let sendData = {
        account_id: this.mailList[0].account_id,
        mail_item_ids: this.selectedItems
      };

      if (this.afterCreate == 'reminder' && this.reminderDate)
        sendData.remainder = this.reminderDate; // reminder -> remainder (API issue)

      this.createAdminGroupMail(sendData)
        .then(res => {
          
          if (this.afterCreate == 'forward' && res.id)
            this.$router.push(`group-forwarding?mail_number=${res.id}`);
          else
            this.$router.push('mail?tab=group');

          this.popup.isOpen = false;
          this.showSpinner = false;
        });
    },
  },
  watch: {
    'popup.isOpen'(val) {
      if (!val && this.popup.type == 'pmb' && !this.curPMB)
        this.$router.push('mail?tab=group');

      if (!val && this.popup.type == 'pmb-reselect' && !this.mailList.length)
        this.$router.push('mail?tab=group');
    }
  }
}
</script>

<style lang="scss">
  @use '@/assets/scss/general/mixins.scss' as *;
  @use '@/assets/scss/framework/scss-variables.scss' as *;
  @use '@/assets/scss/pages/common/_mail-group.scss';
</style>